import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Link2,
  Loader2,
  MessageCircle,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import {
  getSmartOrderAdmin,
  saveSmartAdminNotes,
  rebindLatestAttempt,
  generateSmartDraft,
  retrySmartDraft,
  regenerateSmartSection,
  saveSmartSection,
  approveAndSendSmartReport,
  resendSmartReportLink,
  markSmartWhatsAppSent,
  refundSmartOrder,
} from "@/lib/bousala/smart-admin.functions";
import { smartReportDate, type SmartSectionKey } from "@/lib/bousala/smart-order";
import { SmartReportView } from "@/components/bousala/smart/smart-report-view";
import { renderMarkdown } from "@/lib/markdown";
import { arabicDate, arabicDateTimeDamascus } from "@/lib/arabic-format";
import { StatusChip } from "./bousala.smart.index";

const orderQ = (id: string) =>
  queryOptions({
    queryKey: ["admin", "smart", "order", id],
    queryFn: () => getSmartOrderAdmin({ data: { id } }),
  });

export const Route = createFileRoute("/_authenticated/admin/bousala/smart/$id")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(orderQ(params.id)),
  head: () => ({
    meta: [{ title: "طلب البوصلة الذكية — إدارة" }, { name: "robots", content: "noindex" }],
  }),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: SmartOrderPage,
});

const NEEDS_ATTENTION = "يحتاج انتباهاً بشرياً";

function SmartOrderPage() {
  const { id } = Route.useParams();
  const { data: order } = useSuspenseQuery(orderQ(id));
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [notes, setNotes] = useState(order.adminNotes);
  const [send, setSend] = useState<{ waLink: string; magicLink: string } | null>(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [refund, setRefund] = useState<{
    waLink: string;
    message: string;
    hasPhone: boolean;
  } | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "smart", "order", id] });

  async function act(key: string, fn: () => Promise<unknown>, okMsg?: string) {
    setBusy(key);
    try {
      const res: any = await fn();
      if (res && res.ok === false) toast.error(res.reason ?? "لم تنجح العملية.");
      else if (okMsg) toast.success(okMsg);
      await refresh();
      return res;
    } catch (e: any) {
      toast.error(e?.message ?? "حدث خطأ.");
      return null;
    } finally {
      setBusy(null);
    }
  }

  const sectionsMap = Object.fromEntries(order.sections.map((s) => [s.key, s.body])) as Partial<
    Record<SmartSectionKey, string>
  >;
  const allFilled = order.sections.every((s) => s.body.trim().length > 0);

  return (
    <div className="space-y-5">
      <Link
        to="/admin/bousala/smart"
        className="inline-flex items-center gap-1 text-sm font-bold text-muted-foreground hover:text-foreground"
      >
        <ArrowRight className="size-4" aria-hidden />
        كل الطلبات
      </Link>

      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">{order.buyerFirstName || "بلا اسم"}</h1>
          <p className="text-sm text-muted-foreground">
            أُنشئ {arabicDate(order.createdAt)}
            {order.slaDueAt ? ` · الموعد ${arabicDateTimeDamascus(order.slaDueAt)}` : ""}
          </p>
        </div>
        <StatusChip status={order.status} />
      </header>

      {order.adminNotes.includes(NEEDS_ATTENTION) ? (
        <div className="flex items-start gap-2 rounded-2xl border border-rose-300 bg-rose-50 p-4 text-sm text-rose-900">
          <AlertTriangle className="mt-0.5 size-5 shrink-0" aria-hidden />
          <p>
            هذا الطلب يحتاج انتباهاً بشرياً قبل الإرسال. راجع الملاحظات الإدارية أدناه. هذا التنبيه
            لا يظهر للمشتري إطلاقاً.
          </p>
        </div>
      ) : null}

      {order.duplicateOrders > 1 ? (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          لهذا العميل {order.duplicateOrders} طلبات «بوصلة ذكية». تأكد من أنك تعمل على الطلب الصحيح.
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardContent className="space-y-3 p-5">
            <h2 className="font-display text-lg font-bold">نموذج التفضيلات</h2>
            <Row label="لغة التقرير" value={order.intake.reportLanguage} />
            <Row label="بلد الدراسة" value={order.intake.targetCountry} />
            <Row label="السياق المالي" value={order.intake.financialContext} />
            <Row label="التخصصات المطروحة" value={order.intake.majors.join("، ") || "—"} />
            <Free label="توقعات الأهل" value={order.intake.familyExpectations} />
            <Free label="الأهداف" value={order.intake.futureGoals} />
            <Free label="المخاوف" value={order.intake.concerns} />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-3 p-5">
            <h2 className="font-display text-lg font-bold">نتيجة «بوصلة د. سارة»</h2>
            {order.results ? (
              <>
                <Row label="البصمة" value={order.results.fingerprint} />
                <Row label="الرمز المهني" value={order.results.hollandCode} />
                <Row label="الذكاءات" value={order.results.intelligences.join("، ")} />
                <Row
                  label="الشخصية"
                  value={order.results.personality.map((p) => `${p.trait}: ${p.band}`).join("، ")}
                />
                <Row label="مهارات قوية" value={order.results.skillsStrong.join("، ") || "—"} />
                <Row label="مهارات للتطوير" value={order.results.skillsDevelop.join("، ") || "—"} />
                <Row label="القيم" value={order.results.valuesTop.join("، ") || "—"} />
                <Row label="الجاهزية" value={`${order.results.readinessPct}`} />
                <Row label="مؤشرات" value={order.results.flags.join("، ") || "—"} />
                <Row label="الثقة" value={order.results.confidence} />
                <div className="space-y-1 pt-2 text-sm">
                  {Object.entries(order.results.majorsByTier).map(([tier, list]) => (
                    <p key={tier}>
                      <span className="font-bold">{tier}:</span>{" "}
                      {list.map((m) => `${m.name} (${m.match})`).join("، ") || "—"}
                    </p>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">لا توجد محاولة مصححة مربوطة بهذا الطلب.</p>
            )}
            {order.canRebind ? (
              <Button
                variant="outline"
                disabled={busy !== null}
                onClick={() => act("rebind", () => rebindLatestAttempt({ data: { id } }), "تم الربط.")}
              >
                <Link2 className="ms-2 size-4" aria-hidden />
                ربط آخر محاولة مكتملة
              </Button>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="space-y-3 p-5">
          <h2 className="font-display text-lg font-bold">الدفع</h2>
          {order.purchase ? (
            <>
              <Row label="الحالة" value={order.purchase.status} />
              <Row label="الطريقة" value={order.purchase.paymentMethod ?? "—"} />
              <Row label="البيئة" value={order.purchase.environment ?? "—"} />
            </>
          ) : (
            <p className="text-sm text-muted-foreground">لا توجد عملية دفع مرتبطة.</p>
          )}

          {order.canRefund ? (
            <div className="space-y-2 border-t pt-4">
              {!refundOpen ? (
                <>
                  <Button
                    variant="outline"
                    className="border-rose-300 text-rose-700 hover:bg-rose-50"
                    disabled={busy !== null}
                    onClick={() => setRefundOpen(true)}
                  >
                    تسجيل استرداد
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    هذا تسجيل داخلي فقط. الإعادة الفعلية للمبلغ تتم يدوياً عبر وسيلة الدفع نفسها.
                  </p>
                </>
              ) : (
                <div className="space-y-2 rounded-2xl border border-rose-300 bg-rose-50 p-4">
                  <p className="text-sm font-bold text-rose-900">تأكيد الاسترداد</p>
                  <p className="text-xs text-rose-900/80">
                    سيُحوَّل الطلب والدفعة إلى «مسترد»، ولن يعود المشتري يرى التقرير. لا يُرسل أي طلب إلى
                    مزوّد الدفع من هنا.
                  </p>
                  <Textarea
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    rows={3}
                    placeholder="سبب الاسترداد (مطلوب)"
                    aria-label="سبب الاسترداد"
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="destructive"
                      disabled={busy !== null || refundReason.trim().length < 3}
                      onClick={async () => {
                        const res: any = await act(
                          "refund",
                          () => refundSmartOrder({ data: { id, reason: refundReason.trim() } }),
                          "سُجّل الاسترداد.",
                        );
                        if (res?.ok) {
                          setRefund(res);
                          setRefundOpen(false);
                          setRefundReason("");
                        }
                      }}
                    >
                      نعم، سجّل الاسترداد
                    </Button>
                    <Button variant="ghost" disabled={busy !== null} onClick={() => setRefundOpen(false)}>
                      لا، تراجع
                    </Button>
                  </div>
                </div>
              )}

              {refund ? (
                <div className="space-y-2 rounded-2xl border p-4">
                  {refund.hasPhone && refund.waLink ? (
                    <a
                      href={refund.waLink}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-bold text-white"
                    >
                      <MessageCircle className="size-4" aria-hidden />
                      إبلاغ المشتري على واتساب
                    </a>
                  ) : (
                    <p className="text-sm text-muted-foreground">لا يوجد رقم واتساب — انسخ الرسالة:</p>
                  )}
                  <p className="rounded-xl bg-muted p-3 text-xs">{refund.message}</p>
                </div>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-5">
          <h2 className="font-display text-lg font-bold">ملاحظات إدارية</h2>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={5} />
          <Button
            variant="outline"
            disabled={busy !== null}
            onClick={() => act("notes", () => saveSmartAdminNotes({ data: { id, notes } }), "حُفظت.")}
          >
            حفظ الملاحظات
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-5">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold">
            <Sparkles className="size-4" aria-hidden />
            المسودة
          </h2>
          {order.generateBlockedReason && !order.canGenerate ? (
            <p className="text-sm text-muted-foreground">{order.generateBlockedReason}</p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={!order.canGenerate || busy !== null}
              onClick={() =>
                act("gen", () => generateSmartDraft({ data: { id } }), "جاهزة للمراجعة.")
              }
            >
              {busy === "gen" ? <Loader2 className="ms-2 size-4 animate-spin" aria-hidden /> : null}
              توليد المسودة
            </Button>
            {order.status === "stalled" || order.generationStuck ? (
              <Button
                variant="outline"
                disabled={busy !== null}
                onClick={() => act("retry", () => retrySmartDraft({ data: { id } }), "أُعيدت المحاولة.")}
              >
                <RefreshCw className="ms-2 size-4" aria-hidden />
                إعادة المحاولة
              </Button>
            ) : null}
          </div>
          {order.promptVersion ? (
            <p className="text-xs text-muted-foreground">إصدار الصياغة: {order.promptVersion}</p>
          ) : null}
        </CardContent>
      </Card>

      {order.status === "ready_for_review" || order.status === "sent" ? (
        <>
          <Card>
            <CardContent className="space-y-4 p-5">
              <h2 className="font-display text-lg font-bold">الأقسام</h2>
              {order.sections.map((s) => (
                <SectionEditor
                  key={s.key}
                  id={id}
                  sectionKey={s.key}
                  title={s.title}
                  body={s.body}
                  editedAt={s.editedAt}
                  disabled={busy !== null}
                  onDone={refresh}
                />
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 p-5">
              <h2 className="font-display text-lg font-bold">كما سيراه المشتري</h2>
              <SmartReportView
                header={{
                  clientName: order.buyerFirstName,
                  code: order.results?.hollandCode ?? "—",
                  deliveredAt: smartReportDate(order.sentAt ?? new Date(), order.reportLanguage),
                  consideredMajors: order.intake.majors,
                  targetCountry: order.intake.targetCountry,
                }}
                sections={sectionsMap}
                lang={order.reportLanguage}
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 p-5">
              <h2 className="font-display text-lg font-bold">الإرسال</h2>
              {order.status === "ready_for_review" ? (
                <Button
                  disabled={!allFilled || busy !== null}
                  onClick={async () => {
                    const res: any = await act(
                      "send",
                      () => approveAndSendSmartReport({ data: { id } }),
                      "أُرسل التقرير.",
                    );
                    if (res?.magicLink) setSend(res);
                  }}
                >
                  <CheckCircle2 className="ms-2 size-4" aria-hidden />
                  اعتماد وإرسال
                </Button>
              ) : (
                <Button
                  variant="outline"
                  disabled={busy !== null}
                  onClick={async () => {
                    const res: any = await act("resend", () => resendSmartReportLink({ data: { id } }));
                    if (res?.magicLink) setSend(res);
                  }}
                >
                  إعادة إرسال الرابط
                </Button>
              )}

              {send ? (
                <div className="space-y-2 rounded-2xl border p-4">
                  {order.hasPhone && send.waLink ? (
                    <a
                      href={send.waLink}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => markSmartWhatsAppSent({ data: { id } })}
                      className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-bold text-white"
                    >
                      <MessageCircle className="size-4" aria-hidden />
                      فتح واتساب
                    </a>
                  ) : (
                    <p className="text-sm text-muted-foreground">لا يوجد رقم واتساب — انسخ الرابط:</p>
                  )}
                  <p className="break-all rounded-xl bg-muted p-3 text-xs">{send.magicLink}</p>
                </div>
              ) : null}

              {order.waSentAt ? (
                <p className="text-xs text-muted-foreground">
                  أُرسل على واتساب {arabicDateTimeDamascus(order.waSentAt)}
                </p>
              ) : null}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <p className="text-sm">
      <span className="font-bold">{label}:</span> {value}
    </p>
  );
}

function Free({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-sm">
      <p className="font-bold">{label}</p>
      <p className="whitespace-pre-wrap text-foreground/80">{value || "—"}</p>
    </div>
  );
}

function SectionEditor({
  id,
  sectionKey,
  title,
  body,
  editedAt,
  disabled,
  onDone,
}: {
  id: string;
  sectionKey: SmartSectionKey;
  title: string;
  body: string;
  editedAt: string | null;
  disabled: boolean;
  onDone: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(body);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await saveSmartSection({ data: { id, key: sectionKey, body: draft } });
      toast.success("حُفظ القسم.");
      setEditing(false);
      onDone();
    } catch (e: any) {
      toast.error(e?.message ?? "تعذّر الحفظ.");
    } finally {
      setBusy(false);
    }
  }

  async function regenerate() {
    setBusy(true);
    try {
      const res: any = await regenerateSmartSection({ data: { id, key: sectionKey } });
      if (!res.ok) toast.error(res.reason);
      else {
        setDraft(res.body);
        toast.success("أُعيد توليد القسم.");
        onDone();
      }
    } catch (e: any) {
      toast.error(e?.message ?? "تعذّر التوليد.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-2xl border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-bold">{title}</h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={disabled || busy} onClick={() => setEditing((v) => !v)}>
            {editing ? "إغلاق" : "تعديل"}
          </Button>
          <Button size="sm" variant="outline" disabled={disabled || busy} onClick={regenerate}>
            {busy ? <Loader2 className="ms-2 size-4 animate-spin" aria-hidden /> : null}
            إعادة توليد هذا القسم
          </Button>
        </div>
      </div>
      {editedAt ? (
        <p className="mt-1 text-xs text-muted-foreground">عُدّل {arabicDateTimeDamascus(editedAt)}</p>
      ) : null}

      {editing ? (
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={14} dir="rtl" />
          <div
            className="prose prose-sm max-w-none rounded-xl bg-muted/40 p-3"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(draft) }}
          />
          <div className="lg:col-span-2">
            <Button size="sm" disabled={busy || !draft.trim()} onClick={save}>
              حفظ القسم
            </Button>
          </div>
        </div>
      ) : (
        <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{body || "— فارغ —"}</p>
      )}
    </div>
  );
}
